import { useCallback, useEffect, useState } from 'react';
import {
  access,
  type Assignment,
  type Role,
  type RoleScope,
  type SaveUser,
  type ScopeTarget,
  type User,
} from '../api/access';
import {
  IconCircleAlert,
  IconKeyRound,
  IconLock,
  IconPencil,
  IconPlus,
  IconRefreshCw,
  IconShieldCheck,
  IconTrash2,
  IconTriangleAlert,
  IconUserRound,
} from '../icons';
import { matches } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Drawer } from '../ui/Drawer';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, PasswordInput, SearchInput, Select, TextInput, Toggle } from '../ui/Field';
import { Card, PageHeader, Toolbar } from '../ui/Page';
import { RowMenu } from '../ui/RowMenu';
import { Table, type Column } from '../ui/Table';

/**
 * The people, and what each one may do where.
 *
 * <p>An assignment is one row — this person, this role, over this region — because the
 * system this replaces splits that question in two and answers each half somewhere else.
 * The user card says somebody is a regional manager; the region record says who its manager
 * is; after three years the two disagree and nothing can say which is right.</p>
 *
 * <p>A password is set by its own action and never as a side effect of saving a name. An
 * account can also have none at all, which is the honest state for one created ahead of an
 * invitation — better than a placeholder somebody has to remember to change.</p>
 */

const SCOPES: RoleScope[] = ['Global', 'Operation', 'Region', 'Location'];

const blank: SaveUser = {
  email: '',
  fullName: '',
  isActive: true,
  isHeadOffice: false,
  initialPassword: null,
};

/**
 * Why this person has this role, in a sentence, for the chip's tooltip.
 *
 * <p>Worth saying out loud because the answer decides what somebody can do about it. A role
 * that came with a seat is taken away by moving them, not from this screen — and this
 * screen used not to show those roles at all, which left it reporting that nearly everybody
 * could do nothing.</p>
 */
function sourceTitle(t: (key: string, args?: Record<string, string>) => string, a: Assignment): string {
  // "Nowhere" and "everywhere" arrive as the same scope value, so the flag decides which
  // this is. Reading a trade's grant as everywhere would be the expensive mistake: it is
  // the one that looks like nothing to fix.
  const where = a.reachesNowhere ? t('users.scope.nowhere') : t(`scope.${a.scope}`);

  return a.source === 'Direct'
    ? `${t('users.source.Direct')} · ${where}`
    : `${t(`users.source.${a.source}`, { through: a.through ?? '' })} · ${where}`;
}

export function Users() {
  const t = useTranslate();
  const [rows, setRows] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [targets, setTargets] = useState<ScopeTarget[]>([]);
  const [term, setTerm] = useState('');
  const [draft, setDraft] = useState<{ id: string | null; values: SaveUser } | null>(null);
  const [granting, setGranting] = useState<User | null>(null);
  const [grant, setGrant] = useState<{ roleId: string; scope: RoleScope; scopeId: string | null }>({
    roleId: '',
    scope: 'Global',
    scopeId: null,
  });
  const [password, setPassword] = useState<{ user: User; value: string } | null>(null);
  const [doomed, setDoomed] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [list, roleList, scopeTargets] = await Promise.all([
        access.users.list(),
        access.roles.list(),
        access.scopeTargets(),
      ]);
      setRows(list);
      setRoles(roleList);
      setTargets(scopeTargets);
      setFailed(false);
      setError(null);
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Keep the open panel showing the user as the server last described them.
  useEffect(() => {
    if (granting) setGranting(rows.find((u) => u.id === granting.id) ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const values = draft?.values;
  const set = (change: Partial<SaveUser>) =>
    setDraft((d) => (d ? { ...d, values: { ...d.values, ...change } } : d));

  async function act(work: () => Promise<unknown>, onDone?: () => void) {
    if (busy) return;

    setBusy(true);
    try {
      await work();
      setPanelError(null);
      setError(null);
      onDone?.();
      await reload();
    } catch (e) {
      const described = describeError(e, 'error.actionFailed');
      if (draft || password || granting) setPanelError(described);
      else setError(described);
    } finally {
      setBusy(false);
    }
  }

  const scopeOptions = targets.filter((x) => x.scope === grant.scope);
  const canGrant = Boolean(grant.roleId && (grant.scope === 'Global' || grant.scopeId));
  const shown = term.trim() ? rows.filter((row) => matches(`${row.fullName} ${row.email}`, term)) : rows;

  const columns: Column<User>[] = [
    {
      id: 'name',
      header: t('common.name'),
      className: 'is-primary',
      cell: (row) => row.fullName,
      skeletonWidth: '58%',
    },
    {
      id: 'email',
      header: t('users.email'),
      cell: (row) => <span className="nv-code">{row.email}</span>,
      skeletonWidth: '72%',
    },
    {
      id: 'roles',
      header: t('users.roles'),
      cell: (row) =>
        row.assignments.length === 0 ? (
          <Badge tone="na" icon={IconTriangleAlert}>{t('users.noRoles')}</Badge>
        ) : (
          <span className="nv-badge-group">
            {row.assignments.map((a) => (
              // The chart's roles and the person's own look different on purpose: one is
              // read off where somebody sits, the other is an exception somebody made.
              <Badge
                key={`${a.roleId}:${a.source}:${a.scopeId ?? ''}`}
                tone={a.source === 'Direct' ? 'comment' : 'scale'}
                title={sourceTitle(t, a)}
              >
                {a.scopeName ? `${a.roleName} · ${a.scopeName}` : a.roleName}
              </Badge>
            ))}
          </span>
        ),
      skeletonWidth: '80%',
    },
    {
      id: 'status',
      header: t('common.status'),
      cell: (row) => (
        <span className="nv-badge-group">
          <Badge tone={row.isActive ? 'comment' : 'neutral'}>
            {row.isActive ? t('common.active') : t('common.inactive')}
          </Badge>
          {/* Where a finding can be sent, which is not visible anywhere else on this row. */}
          {row.isHeadOffice ? (
            <Badge tone="scale" shape="square">{t('users.headOfficeShort')}</Badge>
          ) : null}
          {/* Cannot sign in at all, which is not the same as being switched off. */}
          {row.hasPassword ? null : (
            <Badge tone="na" icon={IconKeyRound}>{t('users.noPassword')}</Badge>
          )}
          {row.lockedUntil ? <Badge tone="critical" icon={IconLock}>{t('users.locked')}</Badge> : null}
        </span>
      ),
      skeletonWidth: '76px',
    },
  ];

  return (
    <>
      <PageHeader
        crumbs={[t('app.people'), t('nav.users')]}
        title={t('users.title')}
        subtitle={t('users.subtitle')}
        action={
          <Button
            variant="primary"
            icon={IconPlus}
            onClick={() => {
              setPanelError(null);
              setDraft({ id: null, values: { ...blank } });
            }}
          >
            {t('users.add')}
          </Button>
        }
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card className="nv-table-card">
        <Toolbar>
          <SearchInput value={term} onValueChange={setTerm} placeholder={t('users.search')} />
        </Toolbar>

        <Table
          caption={t('users.title')}
          columns={columns}
          rows={shown}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          rowActions={(row) => (
            <>
              <IconButton
                label={t('common.edit')}
                icon={IconPencil}
                onClick={() => {
                  setPanelError(null);
                  setDraft({
                    id: row.id,
                    values: {
                        email: row.email,
                        fullName: row.fullName,
                        isActive: row.isActive,
                        isHeadOffice: row.isHeadOffice,
                      },
                  });
                }}
              />
              <RowMenu
                label={t('common.moreActions')}
                items={[
                  {
                    label: t('users.manageRoles'),
                    icon: IconShieldCheck,
                    onSelect: () => {
                      setPanelError(null);
                      setGrant({ roleId: '', scope: 'Global', scopeId: null });
                      setGranting(row);
                    },
                  },
                  {
                    label: t('users.setPassword'),
                    icon: IconKeyRound,
                    onSelect: () => {
                      setPanelError(null);
                      setPassword({ user: row, value: '' });
                    },
                  },
                  { label: t('users.delete'), icon: IconTrash2, danger: true, onSelect: () => setDoomed(row) },
                ]}
              />
            </>
          )}
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={<Button icon={IconRefreshCw} onClick={() => void reload()}>{t('common.retry')}</Button>}
            />
          }
          emptyState={
            <EmptyState
              icon={IconUserRound}
              title={t('users.empty')}
              text={t('users.emptyText')}
              action={
                <Button variant="primary" icon={IconPlus} onClick={() => setDraft({ id: null, values: { ...blank } })}>
                  {t('users.add')}
                </Button>
              }
            />
          }
        />
      </Card>

      {/* ---------------------------------------------------------------- the card */}
      <Drawer
        open={draft !== null}
        title={t(draft?.id ? 'users.editTitle' : 'users.newTitle')}
        onClose={() => setDraft(null)}
        footer={
          <>
            <Button onClick={() => setDraft(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={busy || !values?.fullName.trim() || !values?.email.trim()}
              loading={busy}
              loadingText={t('common.saving')}
              onClick={() =>
                draft &&
                values &&
                void act(
                  () => (draft.id ? access.users.update(draft.id, values) : access.users.create(values)),
                  () => setDraft(null),
                )
              }
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {draft && values ? (
          <>
            <Field label={t('common.name')} required>
              {({ id }) => (
                <TextInput id={id} value={values.fullName} onChange={(e) => set({ fullName: e.target.value })} />
              )}
            </Field>

            <Field label={t('users.email')} hint={t('users.emailHint')} required>
              {({ id, describedBy }) => (
                <TextInput
                  id={id}
                  aria-describedby={describedBy}
                  type="email"
                  autoComplete="off"
                  value={values.email}
                  onChange={(e) => set({ email: e.target.value })}
                />
              )}
            </Field>

            {draft.id ? null : (
              <Field label={t('users.initialPassword')} hint={t('users.initialPasswordHint')}>
                {({ id, describedBy }) => (
                  <PasswordInput
                    id={id}
                    aria-describedby={describedBy}
                    autoComplete="new-password"
                    value={values.initialPassword ?? ''}
                    onChange={(e) => set({ initialPassword: e.target.value || null })}
                  />
                )}
              </Field>
            )}

            <div className="nv-drawer__section">
              <Toggle
                checked={values.isHeadOffice}
                onCheckedChange={(isHeadOffice) => set({ isHeadOffice })}
                label={t('users.isHeadOffice')}
              />
              <p className="nv-hint">{t('users.isHeadOfficeHint')}</p>
            </div>

            <div className="nv-drawer__section">
              <Toggle
                checked={values.isActive}
                onCheckedChange={(isActive) => set({ isActive })}
                label={t('users.isActive')}
              />
              <p className="nv-hint">{t('users.isActiveHint')}</p>
            </div>

            {panelError ? <Alert>{panelError}</Alert> : null}
          </>
        ) : null}
      </Drawer>

      {/* ---------------------------------------------------------------- roles */}
      <Drawer
        open={granting !== null}
        title={granting?.fullName ?? ''}
        onClose={() => setGranting(null)}
        footer={<Button onClick={() => setGranting(null)}>{t('common.close')}</Button>}
      >
        {granting ? (
          <>
            <h3 className="nv-h3">{t('users.held')}</h3>
            <p className="nv-hint">{t('users.heldHint')}</p>

            {granting.assignments.length === 0 ? <p className="nv-hint">{t('users.noRolesHint')}</p> : null}

            {granting.assignments.map((a: Assignment) => (
              <div key={`${a.roleId}:${a.source}:${a.scopeId ?? ''}`} className="nv-assignment">
                <div className="nv-assignment__text">
                  <span>{a.roleName}</span>
                  <span className="nv-hint">
                    {sourceTitle(t, a)}
                  </span>
                </div>

                {/* Only what was granted here can be taken away here. A seat's role is
                    undone by moving somebody off the seat, and a button promising
                    otherwise would be one that cannot work. */}
                {a.source === 'Direct' ? (
                  <IconButton
                    label={t('users.revoke')}
                    icon={IconTrash2}
                    tone="danger"
                    disabled={busy}
                    onClick={() => void act(() => access.users.revoke(granting.id, a.id))}
                  />
                ) : null}
              </div>
            ))}

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('users.grant')}</h3>

              <Field label={t('users.role')}>
                {({ id }) => (
                  <Select id={id} value={grant.roleId} onChange={(e) => setGrant({ ...grant, roleId: e.target.value })}>
                    <option value="">{t('common.none')}</option>
                    {roles.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label={t('users.scope')} hint={t('users.scopeHint')}>
                {({ id, describedBy }) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={grant.scope}
                    onChange={(e) => setGrant({ ...grant, scope: e.target.value as RoleScope, scopeId: null })}
                  >
                    {SCOPES.map((s) => (
                      <option key={s} value={s}>{t(`scope.${s}`)}</option>
                    ))}
                  </Select>
                )}
              </Field>

              {grant.scope !== 'Global' ? (
                <Field
                  label={t(`scope.${grant.scope}`)}
                  hint={scopeOptions.length === 0 ? t('users.noScopeTargets') : undefined}
                >
                  {({ id, describedBy }) => (
                    <Select
                      id={id}
                      aria-describedby={describedBy}
                      value={grant.scopeId ?? ''}
                      onChange={(e) => setGrant({ ...grant, scopeId: e.target.value || null })}
                    >
                      <option value="">{t('common.none')}</option>
                      {scopeOptions.map((x) => (
                        <option key={x.id} value={x.id}>{x.name}</option>
                      ))}
                    </Select>
                  )}
                </Field>
              ) : null}

              <Button
                variant="primary"
                icon={IconPlus}
                className="nv-self-start"
                disabled={!canGrant || busy}
                onClick={() =>
                  void act(
                    () => access.users.grant(granting.id, grant),
                    () => setGrant({ roleId: '', scope: 'Global', scopeId: null }),
                  )
                }
              >
                {t('users.grantButton')}
              </Button>
            </div>

            <div className="nv-drawer__section">
              <h3 className="nv-h3">{t('users.effective')}</h3>
              <p className="nv-hint">{t('users.effectiveHint')}</p>

              <div className="nv-badge-group">
                {granting.permissions.length === 0 ? (
                  <span className="nv-hint">{t('common.none')}</span>
                ) : (
                  granting.permissions.map((p) => (
                    <Badge key={p} tone="neutral" shape="square">{t(`permission.${p}`)}</Badge>
                  ))
                )}
              </div>
            </div>

            {panelError ? <Alert>{panelError}</Alert> : null}
          </>
        ) : null}
      </Drawer>

      {/* ---------------------------------------------------------------- password */}
      <Drawer
        open={password !== null}
        title={t('users.setPassword')}
        onClose={() => setPassword(null)}
        footer={
          <>
            <Button onClick={() => setPassword(null)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={busy || (password?.value.length ?? 0) < 12}
              loading={busy}
              loadingText={t('common.saving')}
              onClick={() =>
                password &&
                void act(
                  () => access.users.setPassword(password.user.id, password.value),
                  () => setPassword(null),
                )
              }
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        {password ? (
          <>
            <p className="nv-hint">{password.user.email}</p>

            <Field label={t('users.newPassword')} hint={t('users.passwordHint')}>
              {({ id, describedBy }) => (
                <PasswordInput
                  id={id}
                  aria-describedby={describedBy}
                  autoComplete="new-password"
                  value={password.value}
                  onChange={(e) => setPassword({ ...password, value: e.target.value })}
                />
              )}
            </Field>

            {panelError ? <Alert>{panelError}</Alert> : null}
          </>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={doomed !== null}
        title={doomed ? t('common.deleteTitle', { name: doomed.fullName }) : ''}
        confirmLabel={t('users.delete')}
        onConfirm={() => (doomed ? act(() => access.users.remove(doomed.id)) : Promise.resolve())}
        onClose={() => setDoomed(null)}
      >
        {doomed ? (
          <>
            <p>{doomed.email}</p>
            <p>{t('users.deleteWarning')}</p>
          </>
        ) : null}
      </ConfirmDialog>
    </>
  );
}
